import { formatMoney } from '@/shared/lib/money';
import { Input } from '@/shared/components/ui/input';
import type { ArFacturaLinea, CaeResumen } from '../api';

/**
 * Factura-style preview of a CAE dry-run (imitates the Tecnolegno layout):
 * Datos del comprobante / emisor / cliente, an editable Detalle table (only
 * the descripción is editable — amounts/IVA are server-authoritative), and a
 * Totales band. Branches on the comprobante letra: Factura A discriminates
 * IVA; B/C show prices con IVA + (B) the "IVA Contenido" transparencia line.
 *
 * Shared by the Factura confirm dialog (AdministracionPage) and the Nota de
 * Crédito dialog — the only NC-specific bit is the "Comprobante asociado" row,
 * driven by `resumen.comprobanteAsociado`.
 */
export function CaeFacturaPreview({
  resumen,
  lineas,
  fecha,
  edits,
  onEditLine,
  disabled,
}: {
  resumen: CaeResumen;
  lineas: ArFacturaLinea[] | null;
  fecha: string | null;
  edits: Record<number, string>;
  onEditLine: (index: number, text: string) => void;
  disabled?: boolean;
}) {
  // Factura B/C (and NC B/C) don't discriminate IVA — derived from the last
  // letter of the tipo label ("Factura B", "Nota de Crédito C", …).
  const letra = resumen.tipoComprobante.trim().slice(-1).toUpperCase();
  // Factura E / NC E (exportación): USD, sin IVA, con cotización / incoterm / permiso.
  const ex = resumen.exportacion;
  const cur = ex ? 'USD' : 'ARS';
  const noDiscriminaIva = letra === 'B' || letra === 'C' || ex != null;
  const esB = letra === 'B';
  const asociado = resumen.comprobanteAsociado;

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 pb-2 text-sm">
      {ex && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sky-900">
          <div className="text-xs font-medium">
            Exportación — importes en dólares estadounidenses, sin IVA
          </div>
          <div className="mt-0.5 tabular-nums">
            Cotización declarada a ARCA:{' '}
            <strong>
              {ex.cotizacion.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </strong>{' '}
            · Equivalente en pesos: {formatMoney(ex.importePesos, 'ARS')}
          </div>
        </div>
      )}
      {!ex && resumen.esDolar && resumen.fxRate != null && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          <div className="text-xs font-medium">Cobro en dólares — convertido a pesos</div>
          <div className="mt-0.5 tabular-nums">
            {formatMoney(resumen.montoOriginal, 'USD')} ×{' '}
            {resumen.fxRate.toLocaleString('es-AR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            {' = '}
            <strong>{formatMoney(resumen.cobroPesos, 'ARS')}</strong>
          </div>
        </div>
      )}

      {/* Datos del comprobante */}
      <FacturaSection title="Datos del comprobante">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1">
          <FacturaMeta label="Fecha de emisión" value={fecha} />
          <FacturaMeta label="Comprobante Nº" value="Lo asigna ARCA al emitir" />
          <FacturaMeta
            label="Punto de Venta"
            value={String(resumen.emisor.puntoVenta).padStart(5, '0')}
          />
          <FacturaMeta label="Tipo" value={resumen.tipoComprobante} bold />
          {asociado && (
            <FacturaMeta label="Comprobante asociado" value={asociado.display} bold />
          )}
        </div>
      </FacturaSection>

      {/* Datos del emisor */}
      <FacturaSection title="Datos del emisor">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1">
          <FacturaMeta label="Razón Social" value={resumen.emisor.razonSocial} bold />
          <FacturaMeta label="CUIT" value={resumen.emisor.cuit} />
          <FacturaMeta label="Ingresos Brutos" value={resumen.emisor.ingresosBrutos} />
          <FacturaMeta label="Condición de IVA" value={resumen.emisor.condicionIva} />
          <FacturaMeta label="Dirección Comercial" value={resumen.emisor.domicilio} />
          <FacturaMeta label="Inicio de Actividades" value={resumen.emisor.inicioActividades} />
        </div>
      </FacturaSection>

      {/* Datos del cliente */}
      <FacturaSection title="Datos del cliente">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1">
          <FacturaMeta label="Nombre y Apellido / Razón Social" value={resumen.receptor.nombre} bold />
          <FacturaMeta
            label={ex ? 'ID impositivo' : 'CUIL/CUIT'}
            value={resumen.receptor.documento}
          />
          <FacturaMeta label="Condición de IVA" value={resumen.receptor.condicionIva} />
          <FacturaMeta label="Condición de venta" value={resumen.medioPago} />
          {ex && <FacturaMeta label="País de destino" value={ex.paisDestino} />}
          {ex && <FacturaMeta label="CUIT-país (ARCA)" value={ex.cuitPais} />}
          {ex && <FacturaMeta label="Incoterm" value={ex.incoterm} />}
          {ex && <FacturaMeta label="Permiso de embarque" value={ex.permiso ?? 'Sin permiso'} />}
          <FacturaMeta label="Dirección Comercial" value={resumen.receptor.domicilio} />
        </div>
      </FacturaSection>

      {/* Detalle — solo la descripción (Producto / Servicio) es editable */}
      {lineas && lineas.length > 0 && (
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Detalle</h3>
            <span className="text-[11px] text-muted-foreground">
              Podés editar la descripción; los importes e IVA no se modifican.
            </span>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-2 py-2 text-left font-medium">Código</th>
                  <th className="px-2 py-2 text-left font-medium">Producto / Servicio</th>
                  <th className="px-2 py-2 text-right font-medium">Cantidad</th>
                  <th className="px-2 py-2 text-right font-medium">Precio Unit.</th>
                  <th className="px-2 py-2 text-right font-medium">%Bonif</th>
                  <th className="px-2 py-2 text-right font-medium">Subtotal</th>
                  {!noDiscriminaIva && (
                    <>
                      <th className="px-2 py-2 text-right font-medium">Alíc. IVA</th>
                      <th className="px-2 py-2 text-right font-medium">Subtotal c/IVA</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {lineas.map((l, i) => {
                  const precioConIva =
                    l.cantidad > 0 ? l.importeConIva / l.cantidad : l.importeConIva;
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1.5">{l.codigo || '—'}</td>
                      <td className="px-2 py-1.5 min-w-[220px]">
                        <Input
                          value={edits[i] ?? l.articulo}
                          onChange={(e) => onEditLine(i, e.target.value)}
                          disabled={disabled}
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{l.cantidad}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(noDiscriminaIva ? precioConIva : l.precioUnitario, cur)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{l.descuentoPct}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(noDiscriminaIva ? l.importeConIva : l.importe, cur)}
                      </td>
                      {!noDiscriminaIva && (
                        <>
                          <td className="px-2 py-1.5 text-right tabular-nums">{l.ivaAlicuota}%</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {formatMoney(l.importeConIva, 'ARS')}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Totales */}
      <div className="rounded-lg border bg-muted/50 px-4 py-3">
        {noDiscriminaIva ? (
          <>
            <div className="flex items-center tabular-nums">
              <span className="ml-auto text-base font-bold">
                Importe Total {formatMoney(resumen.importes.total, cur)}
              </span>
            </div>
            {esB && (
              <div className="mt-2 flex items-center justify-between border-t pt-2 text-xs tabular-nums">
                <span className="text-muted-foreground">
                  Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)
                </span>
                <span>
                  <strong>IVA Contenido</strong>{' '}
                  {formatMoney(resumen.importes.iva21 + resumen.importes.iva105, 'ARS')}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 tabular-nums">
            <span>
              <strong>Importe Neto Gravado</strong> {formatMoney(resumen.importes.neto, 'ARS')}
            </span>
            {resumen.importes.iva21 > 0 && (
              <span>
                <strong>IVA 21%</strong> {formatMoney(resumen.importes.iva21, 'ARS')}
              </span>
            )}
            {resumen.importes.iva105 > 0 && (
              <span>
                <strong>IVA 10,5%</strong> {formatMoney(resumen.importes.iva105, 'ARS')}
              </span>
            )}
            {resumen.importes.exento > 0 && (
              <span>
                <strong>Exento</strong> {formatMoney(resumen.importes.exento, 'ARS')}
              </span>
            )}
            <span className="ml-auto text-base font-bold">
              Total {formatMoney(resumen.importes.total, 'ARS')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** A titled section of the factura preview (imitates the Tecnolegno layout). */
function FacturaSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b pb-3">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

/** One "Etiqueta: valor" line inside a FacturaSection. */
function FacturaMeta({
  label,
  value,
  bold,
}: {
  label: string;
  value?: string | null;
  bold?: boolean;
}) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}:</span>{' '}
      <span className={bold ? 'font-medium' : ''}>{value && value !== '-' ? value : '—'}</span>
    </div>
  );
}
